import { Trans, useTranslation } from 'react-i18next';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import { useState, ChangeEvent, FC, useEffect } from 'react';
import { MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import { AdPageType, IAdPage } from '../../interfaces/pages';
import { isOrderedAdType } from '../../utils/string';
import { formatNumberWithComma } from '../../utils/number';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';

type IAdPagesTable = {
	type: AdPageType;
};

const AdPagesTable: FC<IAdPagesTable> = ({ type }) => {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setFetchedData] = useState<IAdPage[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();

	const isOrderedAd = isOrderedAdType(type);

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const properties: (keyof IAdPage)[] = ['handle_name', 'admin_name', 'media'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<IAdPage>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'media',
			headerName: t('platform'),
			flex: 1,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return t(params.row.media);
			},
		},
		{
			field: 'handle_name',
			headerName: t('page_channel_id'),
			flex: 1,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		// Conditionally include the admin_name column
		...(isOrderedAd
			? []
			: [
					{
						field: 'followers_count',
						headerName: t('follower_count'),
						width: 150,
						headerAlign: 'left' as GridAlignment,
						align: 'left' as GridAlignment,
						headerClassName: `${headerClassNames}`,
						renderCell: (params: any) => {
							const formatted = formatNumberWithComma(params.row.followers_count);
							return <Trans i18nKey="follower_count_page_body_cell_label" values={{ formatted }} />;
						},
					},
				]),

		{
			field: 'created_at',
			headerName: t('date'),
			width: isOrderedAd ? 150 : 100,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 150,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: `${headerClassNames} ps-12`,
			renderCell: params => {
				const statusText = params.value ? t('confirmed') : t('unconfirmed');
				const bgColorClass = params.value ? 'bg-success-selected text-success-main' : 'bg-error-selected text-error-main';
				return <div className={`ms-10 mt-4 flex w-[max-content] items-center rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{statusText}</div>;
			},
		},
		// Conditionally include the admin_name column
		// ...(isOrderedAd
		// 	? []
		// 	: [
		// 			{
		// 				field: 'admin_name',
		// 				headerName: t('admin_name'),
		// 				width: 150,
		// 				headerAlign: 'center' as GridAlignment,
		// 				align: 'center' as GridAlignment,
		// 				headerClassName: headerClassNames,
		// 			},
		// 		]),
		// {
		// 	field: 'action',
		// 	headerName: t('action'),
		// 	minWidth: isOrderedAd ? 238 : 138,
		// 	headerAlign: 'center' as GridAlignment,
		// 	align: 'center' as GridAlignment,
		// 	headerClassName: headerClassNames,
		// 	renderCell: params => {
		// 		return (
		// 			<>
		// 				<IconButton aria-label="eye" onClick={handleViewPage(params.row.id.toString())}>
		// 					<VisibilityIcon className="text-text-secondary" />
		// 				</IconButton>
		// 				<IconButton aria-label="eye" onClick={handleViewPage(params.row.id.toString())}>
		// 					<EditIcon className="text-text-secondary" />
		// 				</IconButton>
		// 			</>
		// 		);
		// 	},
		// },
	];

	useEffect(() => {
		setLoading(true);
		axios<IResponse<IAdPage[]>>({
			url: `/media_entities/${isOrderedAd ? 'given' : 'received'}`,
			params: {
				page: page,
				limit: pageSize,
				search: searchTerm,
				field: properties[0],
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, pageSize]);

	return <BaranDataGrid<IAdPage> loading={loading} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} searchableFields={properties} />;
};

export default AdPagesTable;
